import "../../../../css/GlobalStyles.css";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  PlanTypeV4,
  AccountData,
  NotificationType,
  Spreadsheet,
  SpreadsheetImportStatus,
  OnboardingFlowType,
} from "@markit/common.types";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllUserFollowersData,
  fetchAudienceLists,
  getAccountState,
} from "../../../../redux/slices/accountSlice";
import {
  hasSubscription,
  hasSubscriptionUnlockedAdvancedData,
} from "@markit/common.utils";
import EmptyStateButton from "../../../../components/Buttons/EmptyStateButton";
import { Icon } from "@iconify/react";
import { Colors } from "../../../../utils/colors";
import RectangleButtonCreatorPanel from "../../../../components/Buttons/RectangleButtonCreatorPanel";
import { useNavigate } from "../../../../hooks/useNavigate";
import MarkitPlusActionButton from "../../../../components/Subscription/MarkitPlusActionButton";
import CreatorModePanelWrapper from "../../../../components/CreatorDashboard/CreatorModePanelWrapper";
import ImportHistoryItem, {
  IMPORT_HISTORY_FLEX_SECTIONS,
} from "../../../../components/DisplayItem/ImportHistory/ImportHistoryItem";
import SearchBoxContainer from "../../../../components/Containers/SearchBoxContainer";
import { useLiveUpdatingImportAnalytics } from "../../../../hooks/useLiveHooks/useLiveUpdatingImportAnalytics";
import { useLoadSpreadsheetUserList } from "../../../../hooks/useLoadSpreadsheetUserList";
import filter from "lodash.filter";
import ProfileSpreadsheetItem, {
  SPREADSHEET_USER_FLEX_SECTIONS,
} from "../../../../components/DisplayItem/ProfileSpreadsheetItem";
import { EmptyStateFlatlist } from "../../../../components/EmptyStates/EmptyStateFlatlist";
import FullProfilePreviewModal from "../../../../components/FollowerProfile/FullProfilePreviewModal";
import useAsyncOnMount from "../../../../hooks/useAsyncEffectOnMount";
import StandardListContainer from "../../../../components/Containers/StandardListContainer";
import FlatlistLoadMore from "../../../../components/FlatlistLoadMore";
import {
  DropdownMenu,
  DropdownMenuItem,
} from "../../../../components/DropdownMenu";
import AddSingleContactModal from "../../../../components/CreatorDashboard/CreatorProfile/ImportContacts/AddSingleContactModal";
import { notificationActions } from "../../../../redux/slices/notificationSlice";
import EmptyStateButtonSubscription from "../../../../components/Buttons/EmptyState/EmptyStateButtonSubscription";
import { subscribeToUserSpreadsheets } from "../../../../utils/spreadsheetUtils";
import { showNotificationBanner } from "../../../../utils/notificationUtils";
import { useLocation } from "react-router-dom";
import OnboardingFlowDot from "../../../../components/Onboarding/OnboardingFlowDot";

export const IMPORT_HISTORY_TAB_INDEX = 1;

const ImportPanel = () => {
  const { accountData, appInitialized } = useSelector(getAccountState).account;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [loadingFollowers, setLoadingFollowers] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [profilePreviewSelected, setProfilePreviewSelected] =
    useState<AccountData>();
  const [addContactVisible, setAddContactVisible] = useState(false);
  const [tabValue, setTabValue] = useState(
    (location.state as { initialTab?: number } | null)?.initialTab ?? 0
  );
  const [spreadsheets, setSpreadsheets] = useState<Spreadsheet[]>([]);
  const [loadingSpreadsheets, setLoadingSpreadsheets] = useState(true);
  const [historySearchTerm, setHistorySearchTerm] = useState("");

  const styles = {
    optInHeader: {
      paddingBlock: 5,
      fontSize: 14,
      fontWeight: "500",
    },
  };

  const { liveTotalSubscribedImportContacts, loaded } =
    useLiveUpdatingImportAnalytics();

  useEffect(() => {
    const unsubscribe = subscribeToUserSpreadsheets(accountData.uid, (data) => {
      setSpreadsheets(data);
      setLoadingSpreadsheets(false);
    });
    return () => unsubscribe();
  }, [accountData.uid]);

  const inProgressSpreadsheet = useMemo(
    () =>
      spreadsheets.find(
        (s) => s.importStatus === SpreadsheetImportStatus.IN_PROGRESS
      ),
    [spreadsheets]
  );

  const prevInProgressFidRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (prevInProgressFidRef.current && !inProgressSpreadsheet) {
      const finishedSpreadsheet = spreadsheets.find(
        (s) => s.fid === prevInProgressFidRef.current
      );
      const failedImport =
        finishedSpreadsheet?.importStatus === SpreadsheetImportStatus.FAILED;
      showNotificationBanner(
        dispatch,
        failedImport ? "Import Failed" : "Import Completed!",
        failedImport ? NotificationType.NEGATIVE : NotificationType.AFFIRMATIVE
      );
      // Refresh follower and audience list data
      dispatch(fetchAllUserFollowersData(accountData.uid, true));
      dispatch(fetchAudienceLists(accountData.uid));
      setTabValue(1);
    }
    prevInProgressFidRef.current = inProgressSpreadsheet?.fid;
  }, [accountData.uid, inProgressSpreadsheet, spreadsheets, dispatch]);

  const {
    fetchedUserData,
    fetchedFollowerData,
    loadMoreUsers,
    loadUsers,
    loadSearchResultsThrottled,
    isLoading: isLoadingFollowers,
    isFinished: isFinishedLoadingFollowers,
  } = useLoadSpreadsheetUserList({
    userId: accountData.uid,
    windowSize: 30,
  });

  useAsyncOnMount(async () => {
    await loadUsers();
    setLoadingFollowers(false);
  });

  const navigateNewUpload = useCallback(() => {
    if (
      !hasSubscriptionUnlockedAdvancedData(accountData) &&
      !accountData.isAdmin
    ) {
      dispatch(notificationActions.setMarkitPlusModal(PlanTypeV4.PRO.name));
      return;
    }
    navigate("/import");
  }, [accountData, navigate, dispatch]);

  const contains = useCallback((user: AccountData, query: string) => {
    return user.fullName.toLowerCase().includes(query);
  }, []);

  const handleSearch = (text: string) => {
    if (text !== "") {
      loadSearchResultsThrottled(text.toLowerCase());
    }
    setSearchTerm(text.toLowerCase());
  };

  const itemsToShow: AccountData[] = useMemo(() => {
    if (!appInitialized) {
      return [];
    }
    let userList: AccountData[] = [];
    userList = fetchedUserData;
    if (searchTerm !== "") {
      userList = filter(userList, (user: AccountData) => {
        return contains(user, searchTerm);
      });
    }
    return userList;
  }, [appInitialized, fetchedUserData, searchTerm, contains]);

  const renderItem = useCallback(
    (item: AccountData) => (
      <ProfileSpreadsheetItem
        item={item}
        setProfilePreviewSelected={setProfilePreviewSelected}
        fetchedFollowerData={fetchedFollowerData}
      />
    ),
    [fetchedFollowerData, setProfilePreviewSelected]
  );

  const renderEmptyView = useMemo(() => {
    return (
      <EmptyStateFlatlist
        searchTerm={searchTerm}
        isLoading={isLoadingFollowers}
        containerStyles={{ paddingTop: 150 }}
        nonSearchEmptyView={
          !hasSubscriptionUnlockedAdvancedData(accountData) ? (
            <EmptyStateButtonSubscription
              planType={PlanTypeV4.PRO}
              iconName={"ion:cloud-upload"}
              description="Import a CSV to build your audience"
              btnText={
                hasSubscription(accountData) ? "Upgrade to Pro" : "Get Pro Plan"
              }
              containerStyles={{ paddingTop: 150 }}
            />
          ) : (
            <EmptyStateButton
              title="Import Contacts"
              description="Import a CSV to build your audience"
              icon={
                <Icon
                  icon="ion:cloud-upload"
                  height={64}
                  style={{ color: Colors.GRAY1 }}
                />
              }
              iconBox={84}
              btnText="New Import"
              onPress={navigateNewUpload}
              containerStyles={{ paddingTop: 150 }}
            />
          )
        }
      />
    );
  }, [accountData, isLoadingFollowers, navigateNewUpload, searchTerm]);

  const renderImportHistoryItem = useCallback(
    (item: Spreadsheet) => <ImportHistoryItem key={item.fid} item={item} />,
    []
  );

  const handleHistorySearch = (text: string) => {
    setHistorySearchTerm(text.toLowerCase());
  };

  const spreadsheetsToShow = useMemo(() => {
    if (historySearchTerm === "") {
      return spreadsheets;
    }
    return filter(spreadsheets, (item: Spreadsheet) =>
      item.fileName.toLowerCase().includes(historySearchTerm)
    );
  }, [spreadsheets, historySearchTerm]);

  const renderImportHistoryEmptyView = useMemo(() => {
    return (
      <EmptyStateFlatlist
        searchTerm={historySearchTerm}
        isLoading={loadingSpreadsheets}
        containerStyles={{ paddingTop: 150 }}
        nonSearchEmptyView={
          <EmptyStateButton
            title="No Imports Yet"
            description="Your spreadsheet import history will show up here"
            icon={
              <Icon
                icon="ion:cloud-upload"
                height={64}
                style={{ color: Colors.GRAY1 }}
              />
            }
            iconBox={84}
            btnText="New Import"
            onPress={navigateNewUpload}
            containerStyles={{ paddingTop: 150 }}
          />
        }
      />
    );
  }, [loadingSpreadsheets, navigateNewUpload, historySearchTerm]);

  const dropdownItems: DropdownMenuItem[] = useMemo(
    () => [
      {
        title: "Import CSV",
        subtext: !hasSubscriptionUnlockedAdvancedData(accountData) ? (
          <span className="smallBodySubtext">
            Requires Markit
            <span style={{ color: Colors.BLUE5 }}>+ </span>
            Pro Plan
          </span>
        ) : undefined,
        icon: "ion:cloud-upload",
        key: "import_csv",
        onPress: navigateNewUpload,
      },
      {
        title: "Add Single Contact",
        icon: "ion:person-add",
        key: "add_single_contact",
        onPress: () => setAddContactVisible(true),
      },
    ],
    [accountData, navigateNewUpload]
  );

  return (
    <CreatorModePanelWrapper
      title="Import Audience"
      headerRight={
        <div className="AlignedRow" style={{ gap: 14 }}>
          <MarkitPlusActionButton planType={PlanTypeV4.PRO} />
          <DropdownMenu
            dropdownItems={dropdownItems}
            placement="bottomRight"
            altWidth={200}
          >
            <RectangleButtonCreatorPanel
              title="New Import"
              iconName="ion:add-circle"
              onPress={() => {}}
              rightIcon={
                <OnboardingFlowDot
                  flowTypes={[OnboardingFlowType.IMPORT_CONTACTS]}
                />
              }
            />
          </DropdownMenu>
        </div>
      }
      headerTabs={{
        tabNames: ["Overview", "Import History"],
        tabValue: tabValue,
        setTabValue: setTabValue,
      }}
    >
      <div
        className="ColumnNormal"
        style={{ padding: 30, backgroundColor: Colors.WHITE, height: "100%" }}
      >
        {tabValue === 0 ? (
          <StandardListContainer
            searchComp={
              <div className="ColumnNormal" style={{ gap: 14 }}>
                <SearchBoxContainer
                  placeholder="Search"
                  onChange={(e) => handleSearch(e.target.value)}
                  containerStyles={{ marginTop: 0, width: 394 }}
                />
                <span style={styles.optInHeader}>
                  Opted-In ({loaded ? liveTotalSubscribedImportContacts : "--"})
                </span>
              </div>
            }
            gridTable={{
              flexSections: SPREADSHEET_USER_FLEX_SECTIONS,
              sectionTitles: ["Name", "Upload Name", "Phone Number", "Status"],
            }}
            listComp={
              <FlatlistLoadMore
                fullList={fetchedUserData}
                currentList={itemsToShow}
                renderItem={(item: AccountData) => renderItem(item)}
                renderWhenEmpty={renderEmptyView}
                isLoadingMore={isLoadingFollowers}
                showLoadMore={
                  searchTerm.trim() === "" && !isFinishedLoadingFollowers
                }
                loadMoreOnPress={loadMoreUsers}
              />
            }
            isLoading={loadingFollowers || !loaded}
          />
        ) : (
          <StandardListContainer
            searchComp={
              <SearchBoxContainer
                placeholder="Search"
                onChange={(e) => handleHistorySearch(e.target.value)}
                containerStyles={{ marginTop: 0, width: 394 }}
              />
            }
            gridTable={{
              flexSections: IMPORT_HISTORY_FLEX_SECTIONS,
              sectionTitles: ["File", "Contacts", "Status"],
            }}
            listComp={
              <FlatlistLoadMore
                fullList={spreadsheets}
                currentList={spreadsheetsToShow}
                renderItem={(item: Spreadsheet) =>
                  renderImportHistoryItem(item)
                }
                renderWhenEmpty={renderImportHistoryEmptyView}
                isLoadingMore={false}
                showLoadMore={false}
                loadMoreOnPress={async () => {}}
              />
            }
            isLoading={loadingSpreadsheets}
          />
        )}
        <FullProfilePreviewModal
          profileSelected={profilePreviewSelected}
          setProfileSelected={setProfilePreviewSelected}
        />
        {addContactVisible ? (
          <AddSingleContactModal setIsVisible={setAddContactVisible} />
        ) : null}
      </div>
    </CreatorModePanelWrapper>
  );
};

export default ImportPanel;
